import React from 'react';
import { animated } from 'react-spring';
import styled from 'styled-components';
import { FadeUp } from '@nucleus/lib-animation';
import { Portal } from './Portal';

declare module '@nucleus/react-components' {
  interface ReactComponentsTheme {
    Popper?: {
      zIndex?: number;
    };
  }
}

export type PopperPlacement =
  | 'bottom-start'
  | 'bottom-end'
  | 'bottom'
  | 'top-start'
  | 'top-end'
  | 'top'
  | 'left'
  | 'right';

export interface PopperOffset {
  x?: number;
  y?: number;
}

export interface PopperProps {
  /**
   * The element to which the Popper is anchored.
   */
  anchorEl?: HTMLElement | null;
  children?: React.ReactNode;
  className?: string;
  /**
   * When true, the Popper renders in place rather than through a Portal.
   * @default false
   */
  disablePortal?: boolean;
  /**
   * When true, the Popper stays mounted even while closed.
   * Use this for SEO or to maximize responsiveness on open.
   * @default false
   */
  keepMounted?: boolean;
  /**
   * When true, sets the Popper width to match the anchor element's width.
   * Re-applied when the anchor element resizes.
   * @default false
   */
  matchAnchorWidth?: boolean;
  /**
   * Pixel offset applied after positioning. Useful for adding spacing
   * between the Popper and its anchor.
   */
  offset?: PopperOffset;
  /**
   * Whether the Popper is currently shown.
   */
  isOpen?: boolean;
  /**
   * Where the Popper should be positioned relative to the anchor.
   * @default 'bottom-start'
   */
  placement?: PopperPlacement;
  /**
   * Note: `transform` on `style` is reserved for the Popper's positioning
   * engine and will be overwritten. Apply transforms on a child element
   * instead.
   */
  style?: React.CSSProperties;
  /**
   * When false, the Popper appears without the FadeUp animation
   * and stays mounted until `isOpen` flips back to false.
   * @default true
   */
  transition?: boolean;
}

/**
 * The Popper component positions its children relative to an anchor element.
 *
 * Unlike `Popover`, Popper is non-modal: it has no backdrop, doesn't close on
 * scroll, and is intended for inline overlays such as autocomplete listboxes
 * or tooltips.
 *
 * Inspired by [material-ui/popper](https://mui.com/material-ui/react-popper/).
 *
 * @param props - The props for the Popper component.
 * @returns A JSX element representing the Popper, or `null` when closed and not kept mounted.
 */
export const Popper = (props: PopperProps): JSX.Element | null => {
  const { ref } = usePopperPositionedProps(props);

  const wrapInPortal = (node: React.ReactNode) => {
    if (props.disablePortal === true) {
      return <>{node}</>;
    }
    return <Portal>{node}</Portal>;
  };

  if (props.transition === false) {
    if (props.isOpen !== true && props.keepMounted !== true) {
      return null;
    }

    return wrapInPortal(
      <PositionContainer ref={ref}>
        <Content className={props.className} style={props.style}>
          {props.children}
        </Content>
      </PositionContainer>
    );
  }

  return (
    <FadeUp items={props.isOpen === true ? [1] : []}>
      {(springValues) =>
        wrapInPortal(
          <PositionContainer ref={ref}>
            <AnimatedContent
              className={props.className}
              style={{ ...props.style, ...springValues }}
            >
              {props.children}
            </AnimatedContent>
          </PositionContainer>
        )
      }
    </FadeUp>
  );
};

Popper.defaultProps = {
  placement: 'bottom-start' as PopperPlacement,
  transition: true,
  keepMounted: false,
  disablePortal: false,
  matchAnchorWidth: false,
};

/**
 * Outer position-owning element. The positioning engine writes
 * `transform: translate3d(...)` (and optionally `width`) directly to this
 * element on each scroll/resize tick. User-provided `className` / `style` go
 * on the inner `Content` so a user-supplied `style.transform` cannot clobber
 * the engine's positioning.
 *
 * Uses `position: absolute` (not `fixed`) and is portaled into the document
 * so it lives in the body's coordinate space. This makes it scroll naturally
 * with the document — including macOS rubber-band overscroll, which moves
 * document content but skips fixed elements. Matches the default
 * `strategy: 'absolute'` of `@popperjs/core`, which MUI's Popper uses.
 */
const PositionContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: ${({ theme }) => theme._reactComponents.Popper?.zIndex ?? 9000};
`;

const Content = styled.div``;

const AnimatedContent = styled(animated.div)``;

interface PopperOriginAxes {
  vertical: 'top' | 'center' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}

const placementToOrigins = (
  placement: PopperPlacement
): { anchor: PopperOriginAxes; transform: PopperOriginAxes } => {
  switch (placement) {
    case 'top':
      return {
        anchor: { vertical: 'top', horizontal: 'center' },
        transform: { vertical: 'bottom', horizontal: 'center' },
      };
    case 'top-start':
      return {
        anchor: { vertical: 'top', horizontal: 'left' },
        transform: { vertical: 'bottom', horizontal: 'left' },
      };
    case 'top-end':
      return {
        anchor: { vertical: 'top', horizontal: 'right' },
        transform: { vertical: 'bottom', horizontal: 'right' },
      };
    case 'bottom':
      return {
        anchor: { vertical: 'bottom', horizontal: 'center' },
        transform: { vertical: 'top', horizontal: 'center' },
      };
    case 'bottom-end':
      return {
        anchor: { vertical: 'bottom', horizontal: 'right' },
        transform: { vertical: 'top', horizontal: 'right' },
      };
    case 'left':
      return {
        anchor: { vertical: 'center', horizontal: 'left' },
        transform: { vertical: 'center', horizontal: 'right' },
      };
    case 'right':
      return {
        anchor: { vertical: 'center', horizontal: 'right' },
        transform: { vertical: 'center', horizontal: 'left' },
      };
    case 'bottom-start':
    default:
      return {
        anchor: { vertical: 'bottom', horizontal: 'left' },
        transform: { vertical: 'top', horizontal: 'left' },
      };
  }
};

const verticalRatio = (axis: PopperOriginAxes['vertical']) =>
  axis === 'center' ? 0.5 : axis === 'bottom' ? 1 : 0;

const horizontalRatio = (axis: PopperOriginAxes['horizontal']) =>
  axis === 'center' ? 0.5 : axis === 'right' ? 1 : 0;

const flipPlacement = (placement: PopperPlacement): PopperPlacement => {
  switch (placement) {
    case 'bottom':
      return 'top';
    case 'bottom-start':
      return 'top-start';
    case 'bottom-end':
      return 'top-end';
    case 'top':
      return 'bottom';
    case 'top-start':
      return 'bottom-start';
    case 'top-end':
      return 'bottom-end';
    case 'left':
      return 'right';
    case 'right':
      return 'left';
    default:
      return placement;
  }
};

interface UsePopperPositionedReturn {
  ref: (node: HTMLDivElement | null) => void;
}

interface PopperGeometry {
  x: number;
  y: number;
  width?: number;
}

const usePopperPositionedProps = ({
  anchorEl,
  isOpen,
  placement = 'bottom-start',
  offset,
  matchAnchorWidth,
}: PopperProps): UsePopperPositionedReturn => {
  const [element, setElement] = React.useState<HTMLDivElement | null>(null);
  const positionRef = React.useRef<PopperGeometry>({ x: 0, y: 0 });
  const rafRef = React.useRef<number | null>(null);

  // Apply the latest geometry to the DOM. We mutate `style` directly rather
  // than going through React state so each scroll tick lands in the same
  // paint frame as the scroll event itself (no one-frame lag bounce).
  const writeStyle = React.useCallback(() => {
    if (!element) {
      return;
    }
    const { x, y, width } = positionRef.current;
    // Snap to device pixels so subpixel transforms don't blur text on hi-DPI.
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const snappedX = Math.round(x * dpr) / dpr;
    const snappedY = Math.round(y * dpr) / dpr;
    element.style.transform = `translate3d(${snappedX}px, ${snappedY}px, 0)`;
    if (width != null) {
      element.style.width = `${width}px`;
    }
  }, [element]);

  const calculateAndWrite = React.useCallback(() => {
    if (!anchorEl || !element) {
      return;
    }

    const anchorRect = anchorEl.getBoundingClientRect();
    const elementWidth = matchAnchorWidth === true ? anchorRect.width : element.offsetWidth;
    const elementHeight = element.offsetHeight;

    const offsetX = offset?.x ?? 0;
    const offsetY = offset?.y ?? 0;

    // Convert the anchor's viewport-relative rect into document coordinates.
    // The popper element is `position: absolute` portaled to <body>, so its
    // CSS `transform` resolves against the body's coordinate space (the
    // document). This means we can position once in document coords and the
    // popper scrolls with the document for free — no per-tick re-write needed
    // during normal scroll, and rubber-band overscroll is also tracked.
    //
    // `getBoundingClientRect()` is relative to the visual viewport on iOS
    // (and the layout viewport elsewhere). `visualViewport.pageTop/pageLeft`
    // is the document-coord position of the visual viewport's top-left, so
    // adding it to the rect always yields the document coord — without
    // double-counting `window.scrollY`, which iOS already shifts when the
    // soft keyboard pans the page.
    const visualViewport = typeof window !== 'undefined' ? window.visualViewport : null;
    const viewportHeight = visualViewport?.height ?? window.innerHeight;
    const viewportPageTop = visualViewport?.pageTop ?? window.scrollY;
    const viewportPageLeft = visualViewport?.pageLeft ?? window.scrollX;

    // Visible area expressed in document coordinates. Used for the flip
    // decision so the popper opens on the side that fits (and excludes the
    // area covered by the iOS soft keyboard, since `vvHeight` shrinks).
    const visibleTopDoc = viewportPageTop;
    const visibleBottomDoc = visibleTopDoc + viewportHeight;

    const anchorTopDoc = anchorRect.top + viewportPageTop;
    const anchorLeftDoc = anchorRect.left + viewportPageLeft;

    const computeFor = (currentPlacement: PopperPlacement) => {
      const { anchor, transform } = placementToOrigins(currentPlacement);
      const top =
        anchorTopDoc +
        anchorRect.height * verticalRatio(anchor.vertical) -
        elementHeight * verticalRatio(transform.vertical) +
        offsetY;
      const left =
        anchorLeftDoc +
        anchorRect.width * horizontalRatio(anchor.horizontal) -
        elementWidth * horizontalRatio(transform.horizontal) +
        offsetX;
      return { top: top, left: left };
    };

    const { top: requestedTop, left } = computeFor(placement);
    let top = requestedTop;

    // Flip placement when the requested side doesn't fit in the visible area
    // and the opposite side does. Position is otherwise free to follow the
    // anchor anywhere it goes, including off-screen (matching MUI's default
    // popperjs behavior, which only prevents overflow on the cross axis).
    if (top + elementHeight > visibleBottomDoc || top < visibleTopDoc) {
      const flipped = computeFor(flipPlacement(placement));
      if (flipped.top >= visibleTopDoc && flipped.top + elementHeight <= visibleBottomDoc) {
        top = flipped.top;
      }
    }

    positionRef.current = {
      x: left,
      y: top,
      width: matchAnchorWidth === true ? anchorRect.width : undefined,
    };
    writeStyle();
  }, [anchorEl, element, placement, offset?.x, offset?.y, matchAnchorWidth, writeStyle]);

  // Coalesce multiple events per frame into a single read+write that lands
  // just before paint.
  const scheduleUpdate = React.useCallback(() => {
    if (rafRef.current != null) {
      return;
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      calculateAndWrite();
    });
  }, [calculateAndWrite]);

  const cancelScheduledUpdate = React.useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return cancelScheduledUpdate;
  }, [cancelScheduledUpdate]);

  React.useEffect(() => {
    if (isOpen !== true) {
      cancelScheduledUpdate();
      return;
    }

    const cleanups: Array<() => void> = [];

    cleanups.push(addEventListeners(['resize'], scheduleUpdate, window));

    // Subscribe to scroll on every scrollable ancestor of the anchor so the
    // popper re-positions when a nested scroll container moves (which never
    // fires `window scroll`). Always include `window` for the page scroll.
    const scrollTargets: Array<EventTarget> = [window];
    if (anchorEl) {
      scrollTargets.push(...getScrollParents(anchorEl));
    }
    scrollTargets.forEach((target) => {
      cleanups.push(addEventListeners(['scroll'], scheduleUpdate, target));
    });

    // iOS fires scroll/resize on the visual viewport (not window) when the
    // soft keyboard pans the page.
    const visualViewport = typeof window !== 'undefined' ? window.visualViewport : null;
    if (visualViewport != null) {
      cleanups.push(addEventListeners(['resize', 'scroll'], scheduleUpdate, visualViewport));
    }

    return () => cleanups.forEach((cleanup) => cleanup());
  }, [isOpen, anchorEl, scheduleUpdate, cancelScheduledUpdate]);

  React.useEffect(() => {
    if (!element) {
      return;
    }
    const observer = new ResizeObserver(() => scheduleUpdate());
    observer.observe(element);
    if (anchorEl) {
      observer.observe(anchorEl);
    }
    return () => observer.disconnect();
  }, [element, anchorEl, scheduleUpdate]);

  // Run synchronously (no rAF) so the popper paints positioned on its very
  // first frame instead of flashing at translate3d(0,0,0).
  React.useLayoutEffect(() => {
    calculateAndWrite();
  }, [calculateAndWrite]);

  return {
    ref: (node: HTMLDivElement | null) => setElement(node),
  };
};

const getScrollParents = (element: HTMLElement): Array<HTMLElement> => {
  const parents: Array<HTMLElement> = [];
  if (typeof window === 'undefined') {
    return parents;
  }
  let current: HTMLElement | null = element.parentElement;
  while (current && current !== document.body && current !== document.documentElement) {
    const computedStyle = window.getComputedStyle(current);
    const overflow = `${computedStyle.overflow}${computedStyle.overflowX}${computedStyle.overflowY}`;
    if (/(auto|scroll|overlay)/.test(overflow)) {
      parents.push(current);
    }
    current = current.parentElement;
  }
  return parents;
};

const addEventListeners = (
  events: Array<string>,
  callback: () => void,
  target: EventTarget = window,
  passive = true
) => {
  events.forEach((event) => target.addEventListener(event, callback, { passive: passive, capture: true }));
  return () =>
    events.forEach((event) => target.removeEventListener(event, callback, { capture: true }));
};
